import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Form } from 'antd';
import type { FormInstance } from 'antd';
import { HtsFlagPriority, HtsInputType } from '@evaltick/shared';
import type {
  HtsClassificationTopic,
  HtsGroupField,
  HtsHardGateGroupCondition,
  HtsReadinessPillarKey,
} from '@evaltick/shared';
import type { HtsQuestionInput } from '@/services/api';
import {
  HTS_CANONICAL_SECTIONS,
  NEEDS_OPTIONS,
  READINESS_PILLAR_OPTIONS,
  generateGroupFieldKey,
  isGroupFieldValid,
  isTargetableGroupField,
} from './htsQuestionFormOptions';
import { WIZARD_STEPS } from './htsQuestionWizardConfig';
import { WizardStepIndicator } from './WizardStepIndicator';
import { HtsQuestionBehavior, deriveBehaviorFromInitialValues } from './htsQuestionBehavior';
import type {
  HtsGroupFieldFormValue,
  HtsHardGateGroupConditionFormValue,
  HtsQuestionFormValues,
} from './htsQuestionFormTypes';
import { Step1Content } from './steps/Step1Content';
import { Step2Section } from './steps/Step2Section';
import { Step3AnswerType } from './steps/Step3AnswerType';
import { Step4Behavior } from './steps/Step4Behavior';
import { Step5Configure } from './steps/Step5Configure';
import { Step6Review } from './steps/Step6Review';
import styles from './HtsQuestionForm.module.scss';

interface HtsQuestionFormProps {
  initialValues: HtsQuestionInput;
  siblingQuestions: HtsQuestionInput[];
  onSubmit: (values: HtsQuestionInput) => void;
  isEditing?: boolean;
  extraSectionOptions?: string[];
}

const LAST_STEP = WIZARD_STEPS.length - 1;

// AntD's `getFieldsValue(true)` returns an untyped snapshot that may be missing
// fields the wizard hasn't reached yet, so callers must treat every field as optional.
const getTypedFieldsValue = <T,>(form: FormInstance): Partial<T> => form.getFieldsValue(true) as Partial<T>;

const hardGateConditionEqualsProvided = (equals: string | string[] | undefined): boolean =>
  Array.isArray(equals) ? equals.length > 0 : Boolean(equals?.trim());

/**
 * Maps a part-filled condition row to the shape the backend accepts — either
 * `noneSelected`, or both halves of a row-field match — or null while the row
 * is still incomplete. Sole definition of "complete", and its two checks are
 * what narrow `groupFieldKey`/`equals` without a cast.
 */
const toApiHardGateCondition = (
  condition: HtsHardGateGroupConditionFormValue
): HtsHardGateGroupCondition | null => {
  if (condition.noneSelected === true) return { noneSelected: true };
  const { groupFieldKey, equals } = condition;
  if (!groupFieldKey || equals === undefined || !hardGateConditionEqualsProvided(equals)) return null;
  return { groupFieldKey, equals };
};

const buildGroupFields = (rows: HtsGroupFieldFormValue[]): HtsGroupField[] => {
  const usedKeys = new Set(rows.map((r) => r.key).filter((k): k is string => Boolean(k?.trim())));
  return rows.map(({ key, label, inputType: fieldInputType, options }): HtsGroupField => ({
    key: key?.trim() ? key : generateGroupFieldKey(label, usedKeys),
    label: label.trim(),
    inputType: fieldInputType,
    options:
      fieldInputType === HtsInputType.SELECT || fieldInputType === HtsInputType.CHECKBOXES
        ? (options ?? []).filter((o) => o?.trim())
        : undefined,
  }));
};

export const HtsQuestionForm: React.FC<HtsQuestionFormProps> = ({
  initialValues,
  siblingQuestions,
  onSubmit,
  isEditing = false,
  extraSectionOptions = [],
}) => {
  const [form] = Form.useForm<HtsQuestionFormValues>();
  const [currentStep, setCurrentStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(isEditing ? LAST_STEP : 0);

  // A single whole-form watch used only to trigger a re-render on any
  // change — the actual values are re-read fresh from the form store below
  // (not from this watch's own value) via getFieldsValue. Per-field
  // Form.useWatch calls each keep their own copy of the value, updated via
  // a separate subscription callback that can land one render behind an
  // unrelated state change in this same component (e.g. clicking a new
  // answer type card and Continue in quick succession): currentStep would
  // advance before the inputType watcher's copy caught up, so stepValid
  // briefly evaluated against the previous type — enabling Continue for a
  // frame before it correctly disabled. Reading getFieldsValue on every
  // render sidesteps that lag since the form store itself is always
  // synchronously current.
  // `preserve: true` matters here: without it this watch only notices
  // registered Form.Item fields, missing a group row's `options` array
  // (set imperatively in GroupFieldOptionsPanel, no Form.Item backing it) —
  // Continue could stay stuck disabled after adding a Dropdown/Checkboxes option.
  Form.useWatch([], { form, preserve: true });
  const {
    inputType,
    text,
    options,
    section,
    behavior,
    classificationTopic,
    hardGateEquals,
    hardGateReason,
    hardGateGroupConditions,
    groupFields,
  } = getTypedFieldsValue<HtsQuestionFormValues>(form);

  const sectionOptions = useMemo(() => {
    const seen = new Set<string>();
    const sections: string[] = [...HTS_CANONICAL_SECTIONS];
    sections.forEach((s) => seen.add(s));
    for (const q of [...siblingQuestions, initialValues]) {
      const trimmed = q.section.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        sections.push(trimmed);
      }
    }
    for (const s of extraSectionOptions) {
      if (s && !seen.has(s)) {
        seen.add(s);
        sections.push(s);
      }
    }
    return sections;
  }, [siblingQuestions, initialValues, extraSectionOptions]);

  const hasValidChoices = !inputType || !NEEDS_OPTIONS.has(inputType) || (options ?? []).some((o) => o?.trim());
  const groupFieldRows = groupFields ?? [];
  const hasValidGroupFields =
    inputType !== HtsInputType.REPEATABLE_GROUP || (groupFieldRows.length > 0 && groupFieldRows.every(isGroupFieldValid));
  const hasValidContent = hasValidChoices && hasValidGroupFields;
  const hardGateEqualsProvided = Array.isArray(hardGateEquals)
    ? hardGateEquals.length > 0
    : Boolean(hardGateEquals?.trim());
  // REPEATABLE_GROUP's hard gate is one or more row-field conditions (OR'd),
  // each needing its own field and value, instead of a single top-level `equals`.
  // Conditions targeting a field deleted in step 3 must also fail validation.
  const targetableGroupFieldKeys = new Set(groupFieldRows.filter(isTargetableGroupField).map((f) => f.key));
  const hardGateGroupConditionsValid =
    (hardGateGroupConditions ?? []).length > 0 &&
    (hardGateGroupConditions ?? []).every(
      (c) =>
        c.noneSelected === true ||
        (hardGateConditionEqualsProvided(c.equals) &&
          c.groupFieldKey !== undefined &&
          targetableGroupFieldKeys.has(c.groupFieldKey))
    );
  const behaviorConfigured =
    behavior === HtsQuestionBehavior.RecordOnly ||
    behavior === HtsQuestionBehavior.Sensitive ||
    ((behavior === HtsQuestionBehavior.Suitability || behavior === HtsQuestionBehavior.Readiness) &&
      Boolean(classificationTopic)) ||
    (behavior === HtsQuestionBehavior.HardGate &&
      Boolean(hardGateReason?.trim()) &&
      (inputType === HtsInputType.REPEATABLE_GROUP ? hardGateGroupConditionsValid : hardGateEqualsProvided));

  const canSave =
    Boolean(inputType) &&
    Boolean(text?.trim()) &&
    hasValidContent &&
    Boolean(section?.trim()) &&
    Boolean(behavior) &&
    behaviorConfigured;

  const stepValid = [
    Boolean(text?.trim()),
    Boolean(section?.trim()),
    Boolean(inputType) && hasValidContent,
    Boolean(behavior),
    behaviorConfigured,
    true,
  ];

  const isLastStep = currentStep === LAST_STEP;
  const canContinue = stepValid[currentStep];

  const readinessPillarsForTopic = (
    topic: HtsClassificationTopic | null | undefined
  ): HtsReadinessPillarKey[] => {
    const matchingPillar = READINESS_PILLAR_OPTIONS.find((p) => p.value === topic);
    return matchingPillar ? [matchingPillar.value] : [];
  };

  const handleSave = () => {
    if (!canSave) return;
    const values = getTypedFieldsValue<HtsQuestionFormValues>(form);
    const {
      behavior: chosenBehavior,
      hardGateEquals: equals,
      hardGatePriority,
      hardGateReason: reason,
      hardGateGroupConditions: conditions,
      groupFields: formGroupFields,
      options: formOptions,
      ...rest
    } = values;

    // canSave already asserts these are populated; this guard just narrows the types for TS.
    const { text: savedText, inputType: savedInputType, section: savedSection } = rest;
    if (!savedText || !savedInputType || !savedSection || !chosenBehavior) return;

    onSubmit({
      ...initialValues,
      ...rest,
      text: savedText,
      inputType: savedInputType,
      section: savedSection,
      options: NEEDS_OPTIONS.has(savedInputType) ? (formOptions ?? []).filter((o) => o?.trim()) : [],
      isSensitive: chosenBehavior === HtsQuestionBehavior.Sensitive,
      classificationTopic:
        chosenBehavior === HtsQuestionBehavior.Suitability || chosenBehavior === HtsQuestionBehavior.Readiness
          ? (rest.classificationTopic ?? null)
          : null,
      readinessPillars:
        chosenBehavior === HtsQuestionBehavior.Sensitive || chosenBehavior === HtsQuestionBehavior.Suitability
          ? []
          : chosenBehavior === HtsQuestionBehavior.Readiness
            ? readinessPillarsForTopic(rest.classificationTopic)
            : (rest.readinessPillars ?? []),
      hardGateRule:
        chosenBehavior === HtsQuestionBehavior.HardGate
          ? savedInputType === HtsInputType.REPEATABLE_GROUP
            ? {
                priority: hardGatePriority ?? HtsFlagPriority.STANDARD,
                reason: reason ?? '',
                groupFieldConditions: (conditions ?? [])
                  .map(toApiHardGateCondition)
                  .filter((c): c is HtsHardGateGroupCondition => c !== null),
              }
            : { equals: equals ?? '', priority: hardGatePriority ?? HtsFlagPriority.STANDARD, reason: reason ?? '' }
          : null,
      groupFields:
        savedInputType === HtsInputType.REPEATABLE_GROUP ? buildGroupFields(formGroupFields ?? []) : null,
    });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1Content />;
      case 1:
        return <Step2Section form={form} sectionOptions={sectionOptions} />;
      case 2:
        return <Step3AnswerType form={form} inputType={inputType} />;
      case 3:
        return <Step4Behavior form={form} />;
      case 4:
        return (
          <Step5Configure
            form={form}
            behavior={behavior}
            inputType={inputType}
            ownOptions={options}
            groupFields={groupFields}
          />
        );
      case 5:
        return <Step6Review form={form} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.wizard}>
      <WizardStepIndicator
        currentStep={currentStep}
        maxReachedStep={maxReachedStep}
        onStepClick={setCurrentStep}
      />

      <div className={styles.stepBody}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onValuesChange={(changedValues) => {
            // Choices/row-fields are specific to the previously-selected
            // answer type — switching type must not leave them behind for
            // the candidate to see under whatever type comes next.
            if ('inputType' in changedValues) {
              form.setFieldsValue({ options: [], groupFields: [], hardGateGroupConditions: [] });
            }
          }}
          initialValues={{
            text: initialValues.text,
            inputType: initialValues.inputType,
            section: initialValues.section,
            helpText: initialValues.helpText,
            options: initialValues.options,
            behavior: deriveBehaviorFromInitialValues(initialValues),
            classificationTopic: initialValues.classificationTopic ?? null,
            readinessPillars: initialValues.readinessPillars ?? [],
            hardGateEquals: initialValues.hardGateRule?.equals,
            hardGatePriority: initialValues.hardGateRule?.priority ?? HtsFlagPriority.STANDARD,
            hardGateReason: initialValues.hardGateRule?.reason,
            hardGateGroupConditions: (initialValues.hardGateRule?.groupFieldConditions ?? []).map((c) => ({
              groupFieldKey: c.groupFieldKey,
              equals: c.equals,
              noneSelected: c.noneSelected,
            })),
            groupFields: (initialValues.groupFields ?? []).map((f) => ({
              key: f.key,
              label: f.label,
              inputType: f.inputType,
              options: f.options ?? [],
            })),
          }}
          className={styles.form}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </Form>
      </div>

      <div className={styles.footer}>
        {currentStep > 0 ? (
          <Button onClick={() => setCurrentStep((s) => s - 1)} className={styles.btnBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        {isEditing || isLastStep ? (
          <Button type="primary" className={styles.btnSave} disabled={!canSave} onClick={handleSave}>
            Save
          </Button>
        ) : (
          <Button
            type="primary"
            className={styles.btnSave}
            disabled={!canContinue}
            onClick={() => {
              const nextStep = Math.min(currentStep + 1, LAST_STEP);
              setCurrentStep(nextStep);
              setMaxReachedStep((m) => Math.max(m, nextStep));
            }}
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};

export default HtsQuestionForm;
